import { useContext, useEffect } from "react";

import { InfoStatusEnum } from "@/components/constants";
import GlobalContext from "@/contexts/Global/GlobalContext";
import MarketingUtils from "@/marketing/marketingUtils";
import { isQuerySuccess } from "@/utils/apolloUtils";

import { isSessionDataValid, isWaitingVideoSession } from "../../utils/sessionValidators";

import type { UseSessionStateManagerProps } from "./types";

/**
 * Hook для управления состоянием сессии в глобальном контексте
 *
 * Отвечает за:
 * - Синхронизацию данных сессии с глобальным состоянием
 * - Обработку изменений статуса сессии
 * - Отправку аналитических событий
 * - Управление показом алертов
 */
export const useSessionStateManager = ({
  sessionData,
  loading,
  error,
  userID,
}: UseSessionStateManagerProps): void => {
  const {
    currentSessionData,
    setCurrentSessionData,
    setAlertStatus,
    setIsAlertShown,
  } = useContext(GlobalContext);

  // Отправка аналитики при получении невалидной сессии
  useEffect(() => {
    if (isQuerySuccess(loading, error) && sessionData) {
      const isValid = isSessionDataValid(sessionData);

      if (!isValid && sessionData.getActiveSession) {
        // Отправляем событие в Яндекс.Метрику
        MarketingUtils.reachYandexMetricsGoal("getActiveSession-zero", userID);
      }
    }
  }, [sessionData, loading, error, userID]);

  // Обновление данных сессии при успешном получении
  useEffect(() => {
    if (isQuerySuccess(loading, error) && sessionData) {
      if (sessionData.getActiveSession) {
        setCurrentSessionData(sessionData.getActiveSession);
        setIsAlertShown(true);
      }
    }
  }, [loading, error, sessionData, setIsAlertShown, setCurrentSessionData]);

  // Обработка исчезновения активной сессии
  useEffect(() => {
    if (isQuerySuccess(loading, error) && sessionData) {
      // Если сервер вернул пустой ответ (сессия завершена)
      if (!sessionData.getActiveSession) {
        // Проверяем, была ли предыдущая сессия в состоянии ожидания видео звонка
        if (isWaitingVideoSession(currentSessionData?.status, currentSessionData?.sessionType)) {
          // Устанавливаем статус "Отменена"
          setAlertStatus(InfoStatusEnum.Cancelled);
        }

        // Очищаем данные сессии в любом случае
        setCurrentSessionData(null);
      }
    }
  }, [
    loading,
    error,
    sessionData,
    currentSessionData,
    setAlertStatus,
    setCurrentSessionData,
  ]);
};
